/**
 * ContributingFactorsDisplay Component
 *
 * Displays contributing factors with visual bars showing relative contribution.
 */

import { Box, Typography } from '@mui/material';

interface ContributingFactor {
  variable: string;
  change_pct: number;
}

interface ContributingFactorsDisplayProps {
  factors: ContributingFactor[];
}

// Factor colors based on type
const FACTOR_COLORS: Record<string, string> = {
  demand_spike: '#ef4444',      // red
  supply_delay: '#f97316',      // orange
  forecast_miss: '#eab308',     // yellow
  inventory_low: '#8b5cf6',     // purple
  lead_time: '#3b82f6',         // blue
  allocation: '#06b6d4',        // cyan
  default: '#6b7280',           // gray
};

const getFactorColor = (factor: string): string => {
  const normalizedFactor = factor.toLowerCase().replace(/\s+/g, '_');
  return FACTOR_COLORS[normalizedFactor] || FACTOR_COLORS.default;
};

const formatFactorLabel = (factor: string): string => {
  return factor
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const ContributingFactorsDisplay = ({ factors }: ContributingFactorsDisplayProps) => {
  if (!factors || factors.length === 0) return null;

  // Find max contribution for scaling bars (use absolute value since change_pct can be negative)
  const maxContribution = Math.max(...factors.map((f) => Math.abs(f.change_pct)));

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 1,
        }}
      >
        Contributing Factors
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {factors.map((factor, index) => {
          const barWidth = maxContribution > 0 ? (Math.abs(factor.change_pct) / maxContribution) * 100 : 0;
          const color = getFactorColor(factor.variable);

          return (
            <Box key={`${factor.variable}-${index}`}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#374151',
                  }}
                >
                  {formatFactorLabel(factor.variable)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: color,
                    fontFamily: '"SF Mono", "Fira Code", Consolas, monospace',
                  }}
                >
                  {factor.change_pct > 0 ? '+' : ''}{factor.change_pct}%
                </Typography>
              </Box>

              {/* Progress bar */}
              <Box
                sx={{
                  height: 6,
                  backgroundColor: '#e5e7eb',
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${barWidth}%`,
                    backgroundColor: color,
                    borderRadius: 3,
                    transition: 'width 0.3s ease-out',
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
